import { useTranslation } from 'react-i18next';
import { RotateCcw, UserPlus, Home } from 'lucide-react';

/**
 * 'link'    — the shared daily-puzzle link resolved to nothing (no puzzle for
 *             that variant today, or the request failed).
 * 'session' — the puzzle id is known but /puzzles/:id/start never succeeded.
 *
 * There used to be a 'no-session' reason, for when the guest login behind a
 * shared link failed. The link no longer opens guest sessions at all (the daily
 * puzzle is registered-only — see DailyPuzzleInvitePage), so nothing can produce
 * it any more.
 */
export type PuzzleUnavailableReason = 'link' | 'session';

interface PuzzleUnavailableProps {
  reason: PuzzleUnavailableReason;
  onRetry?: () => void;
  onHome?: () => void;
  /** Offer "create a free account" — the escape hatch when guests are blocked. */
  onRegister?: () => void;
}

/**
 * What the visitor sees instead of a puzzle when there is nothing to play.
 * Before this existed, those cases rendered the full puzzle layout around a null
 * puzzle — the game store's default starting position on the board, empty stats
 * and inert buttons — which just read as "the app is broken".
 *
 * Deliberately reuses PuzzlePage's `.pz-demo-cta*` / `.pz-btn` classes so it
 * needs no CSS of its own (PuzzlePage.css is eagerly imported by App).
 */
export default function PuzzleUnavailable({
  reason,
  onRetry,
  onHome,
  onRegister,
}: PuzzleUnavailableProps) {
  const { t } = useTranslation();

  const body =
    reason === 'link'
      ? t(
          'puzzle.unavailable.link',
          "We couldn't open today's puzzle. There may be no daily puzzle for this variant, or the connection dropped.",
        )
      : t(
          'puzzle.unavailable.session',
          "We couldn't start this puzzle. Check your connection and try again.",
        );

  return (
    <div className="pz-demo-cta">
      <div className="pz-demo-cta-title">
        {t('puzzle.unavailable.title', "This puzzle isn't available")}
      </div>
      <p className="pz-demo-cta-text">{body}</p>
      {onRetry && (
        <button className="pz-btn pz-primary pz-demo-cta-btn" type="button" onClick={onRetry}>
          <RotateCcw size={16} aria-hidden="true" />
          {t('puzzle.retry', 'Try again')}
        </button>
      )}
      {onRegister && (
        <button className="pz-btn pz-primary pz-demo-cta-btn" type="button" onClick={onRegister}>
          <UserPlus size={16} aria-hidden="true" />
          {t('puzzle.demo.registerCta', 'Create a free account')}
        </button>
      )}
      {onHome && (
        <button className="pz-btn pz-ghost pz-demo-cta-btn" type="button" onClick={onHome}>
          <Home size={16} aria-hidden="true" />
          {t('puzzle.unavailable.home', 'Go to the home page')}
        </button>
      )}
    </div>
  );
}
